// ============================================================================
// stored-document-text.ts
// ----------------------------------------------------------------------------
// Reads the words a project document already carries, for the previews that
// show them (Stage 1 Imported Files and the reference files panel).
//
// Documents reach a project in two shapes:
//   - Stage 1's own drop keeps an email as `content` and `cleanBody`, with
//     `isEmail` and top-level `emailFrom`, `emailTo`, `emailDate` and
//     `emailSubject`. A text file keeps its words as `content`.
//   - Quote Run and File Explorer intake keep an email as `text`: "From:",
//     "To:", "CC:", "Date:" and "Subject:" lines, a blank line, then the body.
//     The headers are also in `metadata.email*`. There is no `isEmail` flag.
//     A text file keeps its words as `text`.
// ============================================================================

const EMAIL_FILE_NAME = /\.(eml|msg)$/i;
const EMAIL_EXTENSIONS = new Set(['eml', 'msg']);
const EMAIL_MIME_TYPES = new Set(['message/rfc822', 'application/vnd.ms-outlook']);
const STORED_EMAIL_HEADER_LINE = /^(from|to|cc|date|subject):[ \t]*(.*)$/i;
const CONTENT_HEADER_LINES = /^(?:Subject|From|To|Date|Cc|Bcc):.*\n/gim;

export interface StoredEmail {
    from: string;
    to: string;
    date: string;
    subject: string;
    body: string;
}

function asText(value: unknown): string {
    return typeof value === 'string' ? value : '';
}

function firstFilled(...values: unknown[]): string {
    const found = values.find(value => typeof value === 'string' && value.trim());
    return typeof found === 'string' ? found.trim() : '';
}

function mimeOf(value: unknown): string {
    return asText(value).split(';')[0]!.trim().toLowerCase();
}

/** An email by its flag, its .eml or .msg name, or its MIME type. */
export function isEmailDocument(doc: any): boolean {
    if (!doc || typeof doc !== 'object') return false;
    if (doc.isEmail) return true;
    if (EMAIL_FILE_NAME.test(asText(doc.name)) || EMAIL_FILE_NAME.test(asText(doc.originalName))) return true;
    if (EMAIL_EXTENSIONS.has(asText(doc.ext).trim().replace(/^\./, '').toLowerCase())) return true;
    return EMAIL_MIME_TYPES.has(mimeOf(doc.contentType)) || EMAIL_MIME_TYPES.has(mimeOf(doc.mimeType));
}

/**
 * Split the leading header lines of an intake email text from its body. Only
 * the leading block is read, so a forwarded message quoted in the body keeps
 * its own From and To lines.
 */
function splitStoredEmailText(value: unknown): { headers: Record<string, string>; body: string } {
    const lines = asText(value).replace(/\r\n?/g, '\n').split('\n');
    const headers: Record<string, string> = {};
    let index = 0;
    for (; index < lines.length; index += 1) {
        const match = (lines[index] || '').match(STORED_EMAIL_HEADER_LINE);
        if (!match) break;
        headers[String(match[1]).toLowerCase()] = String(match[2] || '').trim();
    }
    return { headers, body: lines.slice(index).join('\n').trim() };
}

/**
 * From, To, Date, Subject and body of an email document, from its top-level
 * email* fields, then `metadata.email*`, then the header block of `text`.
 * The body is `cleanBody`, then `content` without its header lines, then
 * `textContent`, then the body of `text`.
 */
export function readStoredEmail(doc: any): StoredEmail {
    const stored = splitStoredEmailText(doc?.text);
    const metadata = doc?.metadata && typeof doc.metadata === 'object' ? doc.metadata : {};
    return {
        from: firstFilled(doc?.emailFrom, metadata.emailFrom, stored.headers.from),
        to: firstFilled(doc?.emailTo, metadata.emailTo, stored.headers.to),
        date: firstFilled(doc?.emailDate, metadata.emailDate, stored.headers.date),
        subject: firstFilled(doc?.emailSubject, metadata.emailSubject, stored.headers.subject),
        body: firstFilled(
            doc?.cleanBody,
            asText(doc?.content).replace(CONTENT_HEADER_LINES, ''),
            doc?.textContent,
            stored.body,
        ),
    };
}

/** The words of a document that is not an email: content, text, textContent, cleanBody. */
export function readStoredDocumentText(doc: any): string {
    return firstFilled(doc?.content, doc?.text, doc?.textContent, doc?.cleanBody);
}
